import axios from 'axios';
import { API_BASE_URL } from '@config';

// Action Types
export const SET_DEVELOPER_CLUSTERS = 'SET_DEVELOPER_CLUSTERS';
export const SET_DEVELOPER_CLUSTERS_LOADING = 'SET_DEVELOPER_CLUSTERS_LOADING';
export const SET_DEVELOPER_CLUSTERS_ERROR = 'SET_DEVELOPER_CLUSTERS_ERROR';
export const SET_DEVELOPER_CLUSTER_LOGS = 'SET_DEVELOPER_CLUSTER_LOGS';
export const SET_DEVELOPER_EXECUTION_RESULT = 'SET_DEVELOPER_EXECUTION_RESULT';
export const CLEAR_DEVELOPER_DATA = 'CLEAR_DEVELOPER_DATA';
export const SET_TERMINAL_CONNECTED = 'SET_TERMINAL_CONNECTED';
export const SET_TERMINAL_CONNECTING = 'SET_TERMINAL_CONNECTING';
export const SET_TERMINAL_SESSION = 'SET_TERMINAL_SESSION';
export const SET_TERMINAL_OUTPUT = 'SET_TERMINAL_OUTPUT';
export const ADD_TERMINAL_OUTPUT = 'ADD_TERMINAL_OUTPUT';
export const CLEAR_TERMINAL_OUTPUT = 'CLEAR_TERMINAL_OUTPUT';
export const SET_TERMINAL_SHORTCUTS = 'SET_TERMINAL_SHORTCUTS';
export const SET_TERMINAL_SHORTCUTS_LOADING = 'SET_TERMINAL_SHORTCUTS_LOADING';
export const SET_TERMINAL_SHORTCUTS_ERROR = 'SET_TERMINAL_SHORTCUTS_ERROR';

// Action Creators
export const setDeveloperClusters = clusters => ({
    type: SET_DEVELOPER_CLUSTERS,
    payload: clusters
});

export const setDeveloperClustersLoading = loading => ({
    type: SET_DEVELOPER_CLUSTERS_LOADING,
    payload: loading
});

export const setDeveloperClustersError = error => ({
    type: SET_DEVELOPER_CLUSTERS_ERROR,
    payload: error
});

export const setDeveloperClusterLogs = logs => ({
    type: SET_DEVELOPER_CLUSTER_LOGS,
    payload: logs
});

export const setDeveloperExecutionResult = result => ({
    type: SET_DEVELOPER_EXECUTION_RESULT,
    payload: result
});

export const clearDeveloperData = () => ({
    type: CLEAR_DEVELOPER_DATA
});

// Terminal Action Creators
export const setTerminalConnected = connected => ({
    type: SET_TERMINAL_CONNECTED,
    payload: connected
});

export const setTerminalConnecting = connecting => ({
    type: SET_TERMINAL_CONNECTING,
    payload: connecting
});

export const setTerminalSession = sessionData => ({
    type: SET_TERMINAL_SESSION,
    payload: sessionData
});

export const setTerminalOutput = output => ({
    type: SET_TERMINAL_OUTPUT,
    payload: output
});

export const addTerminalOutput = outputLine => ({
    type: ADD_TERMINAL_OUTPUT,
    payload: outputLine
});

export const clearTerminalOutput = () => ({
    type: CLEAR_TERMINAL_OUTPUT
});

// Terminal Shortcuts Action Creators
export const setTerminalShortcuts = shortcuts => ({
    type: SET_TERMINAL_SHORTCUTS,
    payload: shortcuts
});

export const setTerminalShortcutsLoading = loading => ({
    type: SET_TERMINAL_SHORTCUTS_LOADING,
    payload: loading
});

export const setTerminalShortcutsError = error => ({
    type: SET_TERMINAL_SHORTCUTS_ERROR,
    payload: error
});

// Async Actions
export const getDeveloperClusters = () => dispatch => {
    dispatch(setDeveloperClustersLoading(true));
    dispatch(setDeveloperClustersError(null));
    
    const request = axios.get(`${API_BASE_URL}/api/dev/clusters`);
    return request
        .then(response => {
            dispatch(setDeveloperClusters(response.data));
            dispatch(setDeveloperClustersLoading(false));
            return response.data;
        })
        .catch(error => {
            console.error('Error fetching developer clusters:', error);
            console.error('Error response:', error.response?.data);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch clusters';
            dispatch(setDeveloperClustersError(errorMessage));
            dispatch(setDeveloperClustersLoading(false));
            throw error;
        });
};

export const getDeveloperCluster = (clusterId) => dispatch => {
    const request = axios.get(`${API_BASE_URL}/api/dev/cluster/${clusterId}`);
    return request
        .then(response => {
            return response.data;
        })
        .catch(error => {
            console.error('Error fetching developer cluster:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch cluster';
            dispatch(setDeveloperClustersError(errorMessage));
            throw error;
        });
};

export const executeDeveloperClusterAction = (actionData) => dispatch => {    
    const request = axios.post(`${API_BASE_URL}/api/dev/cluster-action`, actionData);
    return request
        .then(response => {
            dispatch(setDeveloperExecutionResult(response.data));
            return response.data;
        })
        .catch(error => {
            console.error('Error executing cluster action:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to execute action';
            dispatch(setDeveloperClustersError(errorMessage));
            throw error;
        });
};

export const getDeveloperClusterLogs = (clusterId, logType = 'deploy') => dispatch => {
    // Valid log types: 'init', 'plan', 'deploy', 'destroy', 'master'
    const request = axios.get(`${API_BASE_URL}/api/clusters/logs/${clusterId}/${logType}`);
    return request
        .then(response => {
            const logs = response.data?.logs || '';
            dispatch(setDeveloperClusterLogs(logs));
            return logs;
        })
        .catch(error => {
            console.error('Error fetching cluster logs:', error);
            dispatch(setDeveloperClustersError('Failed to fetch cluster logs'));
            return '';
        });
};

// Terminal Async Actions
export const connectToHeadNode = (clusterId, connectionData) => dispatch => {
    dispatch(setTerminalConnecting(true));
    dispatch(setTerminalConnected(false));
    
    const request = axios.post(`${API_BASE_URL}/api/clusters/${clusterId}/terminal/connect`, connectionData);
    return request
        .then(response => {
            if (response.data.success) {
                dispatch(setTerminalSession({
                    sessionId: response.data.sessionId,
                    clusterId,
                    ...connectionData
                }));
                dispatch(setTerminalConnected(true));
                dispatch(addTerminalOutput({
                    text: `Connected to ${connectionData.user}@${connectionData.host}`,
                    type: 'success',
                    timestamp: new Date().toLocaleTimeString()
                }));
                return response.data;
            } else {
                throw new Error(response.data.error || 'Connection failed');
            }
        })
        .catch(error => {
            console.error('Terminal connection failed:', error);
            dispatch(setTerminalConnected(false));
            dispatch(addTerminalOutput({
                text: `Connection failed: ${error.message}`,
                type: 'error',
                timestamp: new Date().toLocaleTimeString()
            }));
            throw error;
        })
        .finally(() => {
            dispatch(setTerminalConnecting(false));
        });
};

export const disconnectFromHeadNode = (clusterId, sessionId) => dispatch => {
    if (sessionId) {
        const request = axios.post(`${API_BASE_URL}/api/clusters/${clusterId}/terminal/disconnect`, { sessionId });
        request.catch(error => {
            console.error('Error disconnecting terminal:', error);
        });
    }
    
    dispatch(setTerminalConnected(false));
    dispatch(setTerminalConnecting(false));
    dispatch(setTerminalSession(null));
    
    return Promise.resolve();
};

export const executeTerminalCommand = (clusterId, sessionId, command) => dispatch => {
    const request = axios.post(`${API_BASE_URL}/api/clusters/${clusterId}/terminal/execute`, {
        sessionId,
        command
    });
    
    return request
        .then(response => {
            if (response.data.success) {
                const output = response.data.output || '';
                if (output) {
                    dispatch(addTerminalOutput({
                        text: output,
                        type: 'output',
                        timestamp: new Date().toLocaleTimeString()
                    }));
                }
                return response.data;
            } else {
                dispatch(addTerminalOutput({
                    text: `Error: ${response.data.error}`,
                    type: 'error',
                    timestamp: new Date().toLocaleTimeString()
                }));
                throw new Error(response.data.error);
            }
        })
        .catch(error => {
            console.error('Command execution failed:', error);
            dispatch(addTerminalOutput({
                text: `Command failed: ${error.message}`,
                type: 'error',
                timestamp: new Date().toLocaleTimeString()
            }));
            throw error;
        });
};

// Send raw input to terminal (for control sequences, tab completion, etc.)
export const sendTerminalInput = (clusterId, sessionId, input, cols, rows) => dispatch => {
    const requestBody = {
        sessionId,
        input
    };
    
    // Include terminal dimensions if provided
    if (cols && rows) {
        requestBody.cols = cols;
        requestBody.rows = rows;
    }
    
    const request = axios.post(`${API_BASE_URL}/api/clusters/${clusterId}/terminal/input`, requestBody);
    
    return request
        .then(response => {
            if (response.data.success) {
                const output = response.data.output || '';
                if (output) {
                    dispatch(addTerminalOutput({
                        text: output,
                        type: 'output',
                        timestamp: new Date().toLocaleTimeString()
                    }));
                }
                return response.data;
            } else {
                console.warn('Terminal input failed:', response.data.error);
                // Don't add error output for raw input failures
                throw new Error(response.data.error);
            }
        })
        .catch(error => {
            console.error('Terminal input failed:', error);
            // Don't show errors in terminal for raw input
            throw error;
        });
};

// Get shell output for ongoing session
export const getTerminalOutput = (clusterId, sessionId) => dispatch => {
    const request = axios.get(`${API_BASE_URL}/api/clusters/${clusterId}/terminal/${sessionId}/output`);
    
    return request
        .then(response => {
            if (response.data.success) {
                const output = response.data.output || '';
                if (output) {
                    dispatch(addTerminalOutput({
                        text: output,
                        type: 'output',
                        timestamp: new Date().toLocaleTimeString()
                    }));
                }
                return response.data;
            } else {
                console.warn('Get terminal output failed:', response.data.error);
                throw new Error(response.data.error);
            }
        })
        .catch(error => {
            console.error('Get terminal output failed:', error);
            throw error;
        });
};

// Test cluster connectivity from backend
export const testClusterConnectivity = (clusterId, config = null) => dispatch => {
    const requestData = { config };
    
    const request = axios.post(`${API_BASE_URL}/api/dev/cluster/${clusterId}/test-connectivity`, requestData);
    
    return request
        .then(response => {
            return response.data;
        })
        .catch(error => {
            console.error('Connectivity test failed:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Connectivity test failed';
            dispatch(setDeveloperClustersError(errorMessage));
            
            // Return a failed result structure that matches the expected format
            return {
                success: false,
                error: errorMessage,
                results: {
                    isHealthy: false,
                    hasWarnings: true,
                    errors: [{ message: errorMessage, severity: 'error', category: 'system' }],
                    warnings: [],
                    results: [],
                    summary: {
                        total: 1,
                        success: 0,
                        warnings: 0,
                        errors: 1,
                        info: 0,
                        testsRun: 1,
                        testsPlanned: 1
                    }
                }
            };
        });
};

// Update cluster API system - simple wrapper around the main cluster update action
export const updateClusterAPI = (clusterId) => dispatch => {
    const request = axios.post(`${API_BASE_URL}/api/clusters/update-system/${clusterId}`);
    
    return request
        .then(response => {
            return {
                success: true,
                message: response.data.message || 'Cluster API update initiated successfully',
                data: response.data
            };
        })
        .catch(error => {
            console.error('Cluster API update failed:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Cluster API update failed';
            dispatch(setDeveloperClustersError(errorMessage));
            
            return {
                success: false,
                error: errorMessage,
                message: `Failed to update cluster API: ${errorMessage}`
            };
        });
};

// Terminal Shortcuts Async Actions
export const getTerminalShortcuts = () => dispatch => {
    dispatch(setTerminalShortcutsLoading(true));
    dispatch(setTerminalShortcutsError(null));
    
    const request = axios.get(`${API_BASE_URL}/api/settings/terminal/shortcuts`);
    return request
        .then(response => {
            const shortcuts = response.data?.shortcuts || [];
            dispatch(setTerminalShortcuts(shortcuts));
            dispatch(setTerminalShortcutsLoading(false));
            return shortcuts;
        })
        .catch(error => {
            console.error('Error fetching terminal shortcuts:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch shortcuts';
            dispatch(setTerminalShortcutsError(errorMessage));
            dispatch(setTerminalShortcutsLoading(false));
            throw error;
        });
};

export const createTerminalShortcut = (shortcutData) => dispatch => {
    const request = axios.post(`${API_BASE_URL}/api/settings/terminal/shortcuts`, shortcutData);
    return request
        .then(response => {
            const shortcut = response.data?.shortcut;
            if (shortcut) {
                // Refresh the shortcuts list
                dispatch(getTerminalShortcuts());
            }
            return shortcut;
        })
        .catch(error => {
            console.error('Error creating terminal shortcut:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to create shortcut';
            dispatch(setTerminalShortcutsError(errorMessage));
            throw error;
        });
};

export const updateTerminalShortcut = (shortcutId, updateData) => dispatch => {
    const request = axios.put(`${API_BASE_URL}/api/settings/terminal/shortcuts/${shortcutId}`, updateData);
    return request
        .then(response => {
            const shortcut = response.data?.shortcut;
            if (shortcut) {
                // Refresh the shortcuts list
                dispatch(getTerminalShortcuts());
            }
            return shortcut;
        })
        .catch(error => {
            console.error('Error updating terminal shortcut:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to update shortcut';
            dispatch(setTerminalShortcutsError(errorMessage));
            throw error;
        });
};

export const deleteTerminalShortcut = (shortcutId) => dispatch => {
    const request = axios.delete(`${API_BASE_URL}/api/settings/terminal/shortcuts/${shortcutId}`);
    return request
        .then(response => {
            // Refresh the shortcuts list
            dispatch(getTerminalShortcuts());
            return response.data;
        })
        .catch(error => {
            console.error('Error deleting terminal shortcut:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to delete shortcut';
            dispatch(setTerminalShortcutsError(errorMessage));
            throw error;
        });
};
